import { supabase } from '../../../lib/supabase';

const TENANT_ID = '00000000-0000-0000-0000-000000000000';

export const timetableService = {
  // Timetable Setup Operations
  async getTimetableSetup() {
    const { data, error } = await supabase
      .from('saas_myclass_timetable_setup')
      .select('*')
      .eq('tenant_id', TENANT_ID)
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateTimetableSetup(updates) {
    const { data, error } = await supabase
      .from('saas_myclass_timetable_setup')
      .update(updates)
      .eq('tenant_id', TENANT_ID)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Room Operations
  async getRooms() {
    const { data, error } = await supabase
      .from('saas_myclass_room_zones_room_data')
      .select('*')
      .eq('tenant_id', TENANT_ID)
      .order('room_code');
    
    if (error) throw error;
    return data;
  },

  async createRoom(roomData) {
    const { data, error } = await supabase
      .from('saas_myclass_room_zones_room_data')
      .insert({ ...roomData, tenant_id: TENANT_ID })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateRoom(id, updates) {
    const { data, error } = await supabase
      .from('saas_myclass_room_zones_room_data')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async deleteRoom(id) {
    const { error } = await supabase
      .from('saas_myclass_room_zones_room_data')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Teacher Operations
  async getTeachers() {
    const { data, error } = await supabase
      .from('saas_myclass_teachers')
      .select('*')
      .eq('tenant_id', TENANT_ID)
      .order('staff_code');
    
    if (error) throw error;
    return data;
  },

  async createTeacher(teacherData) {
    const { data, error } = await supabase
      .from('saas_myclass_teachers')
      .insert({ ...teacherData, tenant_id: TENANT_ID })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateTeacher(id, updates) {
    const { data, error } = await supabase
      .from('saas_myclass_teachers')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async deleteTeacher(id) {
    const { error } = await supabase
      .from('saas_myclass_teachers')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Class Operations
  async getClasses() {
    const { data, error } = await supabase
      .from('saas_myclass_classes')
      .select('*')
      .eq('tenant_id', TENANT_ID)
      .order('class_code');
    
    if (error) throw error;
    return data;
  },

  async createClass(classData) {
    const { data, error } = await supabase
      .from('saas_myclass_classes')
      .insert({ ...classData, tenant_id: TENANT_ID })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateClass(id, updates) {
    const { data, error } = await supabase
      .from('saas_myclass_classes')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async deleteClass(id) {
    const { error } = await supabase
      .from('saas_myclass_classes')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Subject Groups Operations
  async getSubjectGroups() {
    const { data, error } = await supabase
      .from('saas_myclass_subject_items_groups')
      .select('*')
      .eq('tenant_id', TENANT_ID)
      .order('subject_group_key');
    
    if (error) throw error;
    return data;
  },

  async createSubjectGroup(groupData) {
    const { data, error } = await supabase
      .from('saas_myclass_subject_items_groups')
      .insert({ ...groupData, tenant_id: TENANT_ID })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateSubjectGroup(id, updates) {
    const { data, error } = await supabase
      .from('saas_myclass_subject_items_groups')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async deleteSubjectGroup(id) {
    const { error } = await supabase
      .from('saas_myclass_subject_items_groups')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Class Subject Lessons Operations
  async getClassSubjectLessons() {
    const { data, error } = await supabase
      .from('saas_myclass_class_subject_lessons')
      .select('*')
      .eq('tenant_id', TENANT_ID)
      .order('class_code, subject_code');
    
    if (error) throw error;
    return data;
  },

  async createClassSubjectLesson(lessonData) {
    const { data, error } = await supabase
      .from('saas_myclass_class_subject_lessons')
      .insert({ ...lessonData, tenant_id: TENANT_ID })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateClassSubjectLesson(id, updates) {
    const { data, error } = await supabase
      .from('saas_myclass_class_subject_lessons')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async deleteClassSubjectLesson(id) {
    const { error } = await supabase
      .from('saas_myclass_class_subject_lessons')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Associated Subjects Operations
  async getAssociatedSubjects() {
    const { data, error } = await supabase
      .from('saas_myclass_class_subject_lessons')
      .select('*')
      .eq('tenant_id', TENANT_ID)
      .order('class_code');
    
    if (error) throw error;
    return data;
  },

  async createAssociatedSubject(associationData) {
    const { data, error } = await supabase
      .from('saas_myclass_class_subject_lessons')
      .insert({ ...associationData, tenant_id: TENANT_ID })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateAssociatedSubject(id, associationData) {
    const { data, error } = await supabase
      .from('saas_myclass_class_subject_lessons')
      .update(associationData)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async deleteAssociatedSubject(id) {
    const { error } = await supabase
      .from('saas_myclass_class_subject_lessons')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Data Sets Operations
  async getDataSets() {
    const { data, error } = await supabase
      .from('saas_myclass_data_sets')
      .select('*')
      .eq('tenant_id', TENANT_ID)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async createDataSet(dataSetData) {
    const { data, error } = await supabase
      .from('saas_myclass_data_sets')
      .insert({ ...dataSetData, tenant_id: TENANT_ID })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateDataSet(id, updates) {
    const { data, error } = await supabase
      .from('saas_myclass_data_sets')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async deleteDataSet(id) {
    const { error } = await supabase
      .from('saas_myclass_data_sets')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Timetables Operations
  async getTimetables() {
    const { data, error } = await supabase
      .from('saas_myclass_timetables')
      .select('*')
      .eq('tenant_id', TENANT_ID)
      .order('day_of_week, period_number');
    
    if (error) throw error;
    return data;
  },

  async createTimetable(timetableData) {
    const { data, error } = await supabase
      .from('saas_myclass_timetables')
      .insert({ ...timetableData, tenant_id: TENANT_ID })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateTimetable(id, updates) {
    const { data, error } = await supabase
      .from('saas_myclass_timetables')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async deleteTimetable(id) {
    const { error } = await supabase
      .from('saas_myclass_timetables')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Teacher Timetables Operations
  async getTeacherTimetables() {
    const { data, error } = await supabase
      .from('saas_myclass_teacher_timetables')
      .select('*')
      .eq('tenant_id', TENANT_ID)
      .order('teacher_code, day_of_week, period_number');
    
    if (error) throw error;
    return data;
  },

  async createTeacherTimetable(timetableData) {
    const { data, error } = await supabase
      .from('saas_myclass_teacher_timetables')
      .insert({ ...timetableData, tenant_id: TENANT_ID })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Bulk Operations
  async generateLessons(classSubjectLessons) {
    try {
      // Clear existing timetables
      await supabase
        .from('saas_myclass_timetables')
        .delete()
        .eq('tenant_id', TENANT_ID);

      await supabase
        .from('saas_myclass_teacher_timetables')
        .delete()
        .eq('tenant_id', TENANT_ID);

      // Generate new lessons
      const timetables = [];
      const teacherTimetables = [];
      let lessonId = 1001;

      for (const lesson of classSubjectLessons) {
        for (let i = 0; i < lesson.load_per_cycle; i++) {
          const timetableEntry = {
            lesson_id: lessonId.toString(),
            class_code: lesson.class_code,
            subject_code: lesson.subject_code,
            teacher_code: lesson.teacher_code,
            room_code: lesson.room_code,
            duration: lesson.duration || 1,
            frequency: lesson.frequency || 1,
            concurrent: lesson.concurrent || false,
            remark: lesson.remark || ''
          };

          const teacherTimetableEntry = {
            teacher_code: lesson.teacher_code,
            lesson_id: lessonId.toString(),
            class_code: lesson.class_code,
            subject_code: lesson.subject_code,
            room_code: lesson.room_code,
            duration: lesson.duration || 1,
            concurrent: lesson.concurrent || false
          };

          timetables.push(timetableEntry);
          teacherTimetables.push(teacherTimetableEntry);
          lessonId++;
        }
      }

      // Insert in batches
      const { error: timetablesError } = await supabase
        .from('saas_myclass_timetables')
        .insert(timetables);

      if (timetablesError) throw timetablesError;

      const { error: teacherTimetablesError } = await supabase
        .from('saas_myclass_teacher_timetables')
        .insert(teacherTimetables);

      if (teacherTimetablesError) throw teacherTimetablesError;

      return { success: true, count: timetables.length };
    } catch (error) {
      console.error('Error generating lessons:', error);
      throw error;
    }
  }
};

export default timetableService;
